import React from 'react';
import getConfig from 'next/config';

import {getCategoryBy, getCategoryList, getSubset} from '~/src/utilities/feed/sitemap';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {SitemapEntry} from '~/src/types/data/sitemap';

const {publicRuntimeConfig: config} = getConfig();

interface Sitemap {
    all: SitemapEntry[];
    news: SitemapEntry[];
    video: SitemapEntry[];
    lifestyle: SitemapEntry[];
    isFront: (category: Partial<SitemapEntry>) => boolean;
    getCategoryBy: (query: Partial<SitemapEntry>) => SitemapEntry | null;
}

interface Props {
    sitemap: SitemapEntry[];
    children: React.ReactNode;
}

const SitemapContext = React.createContext<Sitemap>({
    all: [],
    news: [],
    video: [],
    lifestyle: [],
    isFront: () => false,
    getCategoryBy: () => null
});

class SitemapProvider extends React.Component {
    getCategoryBy = (query: Partial<SitemapEntry>) => getCategoryBy(this.state.flattened, query);

    isFront({fullUrlPath}: Partial<SitemapEntry>) {
        return fullUrlPath === config.frontCategory;
    }

    static defaultProps = {
        sitemap: []
    };

    state = {
        flattened: [],
        news: [],
        video: [],
        lifestyle: []
    };

    static getDerivedStateFromProps(props: Props) {
        const flattened = getCategoryList(props.sitemap);
        const news = getSubset(props.sitemap, SiteArea.NEWS);
        const video = getSubset(props.sitemap, SiteArea.VIDEO);
        const lifestyle = getSubset(props.sitemap, SiteArea.LIFESTYLE);

        return {flattened, news, video, lifestyle};
    }

    render() {
        const sitemapData = {
            all: this.state.flattened,
            news: this.state.news,
            video: this.state.video,
            lifestyle: this.state.lifestyle,
            isFront: this.isFront,
            getCategoryBy: this.getCategoryBy
        };
        return <SitemapContext.Provider value={sitemapData}>{this.props.children}</SitemapContext.Provider>;
    }
}

interface ContextProps {
    context: any;
}

interface ContextHoc extends React.ComponentClass<ContextProps> {
    getInitialProps?: () => void;
}

const withSitemapContext = (WrappedComponent: ContextHoc) => {
    const SitemapConsumer = ({context, ...props}: ContextProps) => (
        <SitemapContext.Consumer>{(sitemap) => <WrappedComponent context={{...context, sitemap}} {...props} />}</SitemapContext.Consumer>
    );
    SitemapConsumer.getInitialProps = WrappedComponent.getInitialProps;
    return SitemapConsumer;
};

export {SitemapContext, withSitemapContext};

export default SitemapProvider;
